<template>
  <article
    class="invoice-project-card card border-0 shadow-sm mb-3"
    :class="{ 'has-selection': hasSelection }"
  >
    <div
      class="card-header project-toggle border-0 text-start"
      role="button"
      tabindex="0"
      :aria-expanded="expanded ? 'true' : 'false'"
      @click="expanded = !expanded"
      @keydown.enter.prevent="expanded = !expanded"
      @keydown.space.prevent="expanded = !expanded"
    >
      <div class="d-flex align-items-start justify-content-between flex-wrap gap-3">
        <div class="d-flex align-items-start">
          <span class="project-icon me-3">
            <i class="fa fa-folder-open" />
          </span>
          <span>
            <a
              :href="getProjectUrl(project.id)"
              target="_blank"
              class="d-block project-name project-link"
              @click.stop
            >
              {{ project.project_title }}
            </a>
            <span class="project-meta">Client: {{ project.client_name }}</span>
          </span>
        </div>
        <div class="d-flex align-items-center gap-2">
          <span
            class="badge rounded-pill bg-success-subtle text-success-emphasis"
          >
            {{ project.eligible_expert_count }} eligible
          </span>
          <span class="amount-chip">$ {{ formatAmount(project.total_pending_amount) }}</span>
          <span
            v-if="hasSelection"
            class="selection-badge"
          >
            <i class="fa fa-check-circle me-1" />
            {{ selectedExperts.length }} payment request(s) selected · ${{ formatAmount(selectedTotal) }}
          </span>
          <i
            :class="expanded ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"
            class="chevron"
          />
        </div>
      </div>
    </div>

    <div
      v-if="expanded"
      class="card-body p-0"
    >
      <div
        v-if="loadingExperts"
        class="text-center py-4"
      >
        <div
          class="spinner-border spinner-border-sm text-primary"
          role="status"
        >
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <div
        v-else
        class="table-responsive"
      >
        <table class="table invoice-expert-table mb-0 align-middle">
          <thead>
            <tr>
              <th style="width: 46px;" />
              <th>Expert</th>
              <th>Requested</th>
              <th>Status</th>
              <th class="text-end">
                Pending amount
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="expert in experts"
              :key="expert.payment_request_id"
            >
              <td>
                <input
                  type="checkbox"
                  class="form-check-input"
                  :checked="isSelected(expert.payment_request_id)"
                  @change="toggleExpert(expert.payment_request_id, $event.target.checked)"
                >
              </td>
              <td>
                <a
                  v-if="expert.expert_id"
                  :href="getExpertUrl(expert.expert_id)"
                  target="_blank"
                  class="fw-semibold expert-link"
                  @click.stop
                >{{ expert.name }}</a>
                <span
                  v-else
                  class="fw-semibold"
                >{{ expert.name }}</span>
              </td>
              <td class="text-muted">
                {{ formatRequestedTime(expert.requested_time) }}
              </td>
              <td>
                <span
                  :class="statusClass(expert.status)"
                  class="status-pill"
                >
                  <span class="status-dot">{{ expert.status === "eligible" ? "●" : "◑" }}</span>
                  {{ statusLabel(expert.status) }}
                </span>
              </td>
              <td class="text-end fw-semibold">
                $ {{ formatAmount(expert.pending_amount) }}
              </td>
            </tr>
            <tr v-if="experts.length === 0">
              <td
                colspan="5"
                class="text-center text-muted py-4"
              >
                No eligible experts for this project.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="card-footer selection-footer d-flex flex-wrap align-items-center justify-content-between gap-3">
        <label class="d-flex align-items-center mb-0">
          <input
            class="form-check-input me-2"
            type="checkbox"
            :checked="allSelected"
            :disabled="experts.length === 0 || loadingExperts"
            @change="toggleAll($event.target.checked)"
          >
          Select all
        </label>
        <div class="d-flex align-items-center gap-4 ms-auto">
          <span class="selection-summary">
            <strong>{{ selectedExperts.length }}</strong> payment request(s) selected
            <span class="mx-2 text-muted">|</span>
            <strong>$ {{ formatAmount(selectedTotal) }}</strong>
          </span>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="selectedExperts.length === 0"
            @click="$emit('export', selectedExperts)"
          >
            <i class="fa fa-file-invoice-dollar me-2" />
            Export Invoice
          </button>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import moment from "moment-timezone";

export default {
    name: "InvoiceProjectCard",
    props: {
        project: {
            type: Object,
            required: true,
        },
        selectedExperts: {
            type: Array,
            default: () => [],
        },
        dateFrom: {
            type: String,
            default: "",
        },
        dateTo: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            expanded: false,
            experts: [],
            loadingExperts: false,
            expertsLoaded: false,
        };
    },
    computed: {
        allSelected() {
            return this.experts.length > 0
                && this.experts.every((expert) => this.isSelected(expert.payment_request_id));
        },
        selectedTotal() {
            const selectedIds = new Set(this.selectedExperts.map(Number));

            return this.experts
                .filter((expert) => selectedIds.has(Number(expert.payment_request_id)))
                .reduce((total, expert) => total + Number(expert.pending_amount || 0), 0);
        },
        hasSelection() {
            return this.selectedExperts.length > 0;
        },
    },
    watch: {
        expanded(val) {
            if (val && !this.expertsLoaded) {
                this.fetchExperts();
            }
        },
        dateFrom(newVal, oldVal) {
        },
        dateTo(newVal, oldVal) {
        },
        selectedExperts() {
            this.$emit("selection-amount-change", this.selectedTotal);
        },
    },
    methods: {
        getProjectUrl(projectId) {
            return `/admin/project/details/${projectId}`;
        },
        getExpertUrl(expertId) {
            return `/admin/experts/view/${expertId}`;
        },
        fetchExperts() {
            this.loadingExperts = true;
            const params = {};
            if (this.dateFrom) params.date_from = this.dateFrom;
            if (this.dateTo) params.date_to = this.dateTo;
            axios.get(`/admin/project/${this.project.id}/invoice/eligible`, { params })
                .then(({ data }) => {
                    this.experts = data.data.map((item) => ({
                        payment_request_id: item.payment_request_id,
                        expert_id: item.expert_id,
                        name: item.expert_name,
                        email: item.email,
                        requested_time: item.requested_time,
                        pending_amount: item.amount,
                        status: "eligible",
                    }));
                    this.expertsLoaded = true;
                })
                .finally(() => {
                    this.loadingExperts = false;
                });
        },
        formatAmount(value) {
            return Number(value || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        formatRequestedTime(value) {
            if (!value) {
                return "";
            }

            return moment.utc(value).local().format("DD MMM YYYY");
        },
        isSelected(paymentRequestId) {
            return this.selectedExperts.map(Number).includes(Number(paymentRequestId));
        },
        toggleExpert(paymentRequestId, checked) {
            const selection = new Set(this.selectedExperts.map(Number));

            if (checked) {
                selection.add(Number(paymentRequestId));
            } else {
                selection.delete(Number(paymentRequestId));
            }

            this.$emit("selection-change", Array.from(selection));
        },
        toggleAll(checked) {
            const selection = checked
                ? this.experts.map((expert) => expert.payment_request_id)
                : [];

            this.$emit("selection-change", selection);
        },
        statusClass(status) {
            return status === "eligible" ? "status-eligible" : "status-partial";
        },
        statusLabel(status) {
            return status === "eligible" ? "Eligible" : "Partial";
        },
    },
};
</script>

<style scoped>
.invoice-project-card {
    border-radius: 14px;
    overflow: hidden;
}

.project-toggle {
    background: #fff;
    cursor: pointer;
    padding: 1.15rem 1.25rem;
    width: 100%;
}

.project-link {
    color: #4c78dd;
    text-decoration: none;
}

.project-link:hover {
    color: #3a5fc7;
    text-decoration: underline;
}

.expert-link {
    color: #4c78dd;
    text-decoration: none;
}

.expert-link:hover {
    color: #3a5fc7;
    text-decoration: underline;
}

.project-icon {
    align-items: center;
    background: #fff2d7;
    border-radius: 11px;
    color: #a76a0d;
    display: inline-flex;
    height: 45px;
    justify-content: center;
    width: 45px;
}

.project-name {
    font-size: 1rem;
    font-weight: 700;
}

.project-meta {
    color: #77848b;
    font-size: .83rem;
}

.amount-chip {
    background: #f3f6f7;
    border-radius: 30px;
    color: #233e49;
    font-size: .85rem;
    font-weight: 700;
    padding: .38rem .8rem;
}

.chevron {
    color: #7e8c92;
    margin-left: .4rem;
}

.invoice-expert-table thead th {
    background: #f7f9f8;
    border-bottom: 1px solid #e8ecec;
    color: #718088;
    font-size: .73rem;
    font-weight: 700;
    letter-spacing: .07em;
    padding: .8rem 1.25rem;
    text-transform: uppercase;
}

.invoice-expert-table tbody td {
    border-color: #eef1f1;
    padding: .9rem 1.25rem;
}

.status-pill {
    align-items: center;
    border-radius: 24px;
    display: inline-flex;
    font-size: .8rem;
    font-weight: 600;
    gap: .4rem;
    padding: .32rem .7rem;
}

.status-dot {
    font-size: .72rem;
}

.status-eligible {
    background: #e5f4ec;
    color: #16824c;
}

.status-partial {
    background: #fff2d7;
    color: #a76a0d;
}

.selection-footer {
    background: #fff;
    border-top: 1px solid #ecf0f0;
    padding: .9rem 1.25rem;
}

.selection-summary {
    color: #65747b;
    font-size: .9rem;
}

.has-selection {
    border-left: 4px solid #4c78dd;
}

.has-selection .project-toggle {
    background: #f0f4fd;
}

.selection-badge {
    align-items: center;
    background: #dce8ff;
    border-radius: 20px;
    color: #2557c7;
    display: inline-flex;
    font-size: .78rem;
    font-weight: 700;
    gap: .3rem;
    padding: .3rem .65rem;
}
</style>
